import { assign, fromPromise, setup } from 'xstate';
import { GetEvent } from '~/machines/base-crud';
import { sendCustomError, showAxiosErrorAction } from '../../actions';
import {
  addAttestation,
  editAttestation,
  handleModal,
  loadAttestations,
  loadPdf,
  openDeleteConfirmation,
  resetForm,
  resetIdToDelete,
  sendSaveProgressError,
  sendSaveProgressSuccess,
  setJSONForm,
  setProviderId,
} from './actions';
import { ATTESTATION_API_CALLS, ATTESTATIONS_API_CALLS, FORM_API_CALLS } from './api';
import { DeleteAttestation, Events, PatchAttestation, SubmitAttestation } from './events';
import { Context } from './types';

export const attestationMachine = setup({
  actors: {
    loadForm: fromPromise(FORM_API_CALLS.get),
    getAttestations: fromPromise(ATTESTATIONS_API_CALLS.get),
    sendAnswers: fromPromise(ATTESTATION_API_CALLS.create),
    saveIncompleteAttestation: fromPromise(ATTESTATION_API_CALLS.patch),
    deleteAttestation: fromPromise(ATTESTATION_API_CALLS.delete),
  },
  types: {
    context: {} as Context,
    events: {} as Events,
    input: {} as Partial<Context>,
  },
  actions: {
    setJSONForm: assign(setJSONForm),
    handleModal: assign(handleModal),
    setProviderId: assign(setProviderId),
    loadAttestations: assign(loadAttestations),
    addAttestation: assign(addAttestation),
    resetIdToDelete: assign(resetIdToDelete),
    editAttestation: assign(editAttestation),
    loadPDF: loadPdf,
    resetForm: assign(resetForm),
    sendAxiosError: showAxiosErrorAction,
    sendSaveProgressSuccess,
    sendSaveProgressError,
    openDeleteConfirmation: openDeleteConfirmation,
  },
}).createMachine({
  context: ({ input }) => ({
    loading: false,
    formUi: {},
    form: {},
    modal: false,
    attestations: input.attestations || [],
    providerId: -1,
    sort: {
      sortBy: 'id',
      dateSubmitted: -1,
      id: 1,
      page: 1,
      pageSize: 1,
    },
    attestationFormId: -1,
    attestationType: 'Physician',
    jsonFormId: -1,
    providerType: 'Physician',
    defaultData: undefined,
    idToEdit: undefined,
  }),
  initial: '$_VIEW',
  on: {
    ON_SET_PROVIDER_ID: {
      actions: ['setProviderId'],
      target: '.$_VIEW',
    },
    ON_CLOSE_MODAL: {
      actions: ['handleModal', 'resetForm'],
      target: '.$_VIEW',
    },
    ON_SUBMIT: {
      actions: ['handleModal', assign({ loading: true })],
      target: '.$_SEND_ANSWERS',
    },
    ON_SAVE: {
      target: '.$_SAVE',
    },
  },
  states: {
    $_VIEW: {
      entry: [assign({ loading: false })],
      on: {
        ON_LOAD: {
          target: '$_GET_ATTESTATION',
          actions: [assign({ loading: true })],
        },
        ON_GET_JSON_FORM: {
          target: '$_GET_JSON_FORM',
          actions: [assign({ loading: true })],
        },
        ON_SEND_DELETE_CONFIRMATION: {
          actions: 'openDeleteConfirmation',
        },
        ON_DELETE: {
          target: '$_DELETE',
          actions: [assign({ loading: true })],
        },

        ON_EDIT: {
          target: '$_FORM',
          actions: ['editAttestation', 'handleModal', assign({ loading: true })],
        },
      },
    },
    $_FORM: {},
    $_GET_JSON_FORM: {
      invoke: {
        id: 'loadForm',
        src: 'loadForm',
        input: ({ context, event }) =>
          ({ context, event }) as { context: Context; event: GetEvent },
        onDone: {
          target: `$_FORM`,
          actions: ['setJSONForm', 'handleModal'],
        },
        onError: {
          target: `$_VIEW`,
          actions: [
            ({ system }) =>
              sendCustomError({
                system,
                text: 'Failed to load form',
                subText: 'Unable to retrieve the attestation form. Please try again.',
              }),
          ],
        },
      },
    },
    $_SEND_ANSWERS: {
      invoke: {
        id: 'sendAttestation',
        src: 'sendAnswers',
        input: ({ context, event }) =>
          ({ context, event }) as { context: Context; event: SubmitAttestation },
        onDone: {
          target: '$_GET_ATTESTATION',
          actions: ['loadPDF', 'resetForm', 'resetIdToDelete'],
        },
        onError: {
          target: '$_VIEW',
          actions: ['sendAxiosError'],
        },
      },
    },
    $_GET_ATTESTATION: {
      invoke: {
        id: 'getAttestations',
        src: 'getAttestations',
        input: ({ context, event }) =>
          ({ context, event }) as { context: Context; event: GetEvent },
        onDone: {
          target: '$_VIEW',
          actions: ['loadAttestations'],
        },
        onError: {
          target: '$_VIEW',
          actions: ['sendAxiosError'],
        },
      },
    },
    $_SAVE: {
      entry: [assign({ loading: true })],
      invoke: {
        id: 'patchAttestation',
        src: 'saveIncompleteAttestation',
        input: ({ context, event }) =>
          ({ context, event }) as { context: Context; event: PatchAttestation },
        onDone: {
          target: '$_GET_ATTESTATION',
          actions: ['sendSaveProgressSuccess', 'handleModal', 'resetIdToDelete', 'resetForm'],
        },
        onError: {
          target: '$_FORM',
          actions: ['sendSaveProgressError', assign({ loading: false })],
        },
      },
    },
    $_DELETE: {
      invoke: {
        id: 'deleteAttestation',
        src: 'deleteAttestation',
        input: ({ context, event }) =>
          ({ context, event }) as { context: Context; event: DeleteAttestation },
        onDone: {
          target: '$_GET_ATTESTATION',
        },
        onError: {
          target: '$_VIEW',
        },
      },
    },
  },
});
